import { useEffect, useState } from 'react';
import {
  clockOf,
  GOAL_CHANCES,
  JUMPS,
  jumpForMinute,
  kickoffText,
  phaseOf,
  queuedGoals,
  SCORING_TEAMS,
  timeOf
} from '../labState';
import { Btn, Card, controlClass, Facts, Label, Pill } from '../ui';

const GameControls = ({ game, serverNow, onUpdate, onDelete, busy }) => {
  const [customMinute, setCustomMinute] = useState('');
  const [score, setScore] = useState({ home: '', away: '' });

  // The score boxes start from the live score each time another game is picked.
  useEffect(() => {
    setScore({ home: game.score?.home ?? '0', away: game.score?.away ?? '0' });
    setCustomMinute('');
  }, [game._id]); // eslint-disable-line react-hooks/exhaustive-deps

  const phase = phaseOf(game);
  const queued = queuedGoals(game);
  const forced = !!game.dataInfo?.forcePenaltyShootout;
  const finished = game.status === 'finished';
  const custom = jumpForMinute(customMinute);
  const config = game.mockConfig || {};

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-4">
          <span className="text-[18px] font-semibold text-[#0F172A]">{game.homeName}</span>
          <span className="text-[28px] font-bold tabular-nums text-[#0F172A]">
            {game.score?.home ?? 0}–{game.score?.away ?? 0}
          </span>
          <span className="text-[18px] font-semibold text-[#0F172A]">{game.awayName}</span>
        </div>
        <div className="flex items-center gap-2">
          <Pill tone={phase.tone}>{phase.label}</Pill>
          {clockOf(game) && (
            <span className="text-[15px] font-semibold tabular-nums">{clockOf(game)}</span>
          )}
        </div>
      </div>

      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
        <Facts
          rows={[
            [
              'Kickoff',
              `${new Date(game.date).toLocaleString()} · ${kickoffText(game, serverNow)}`
            ],
            ['HT score', game.htScore ? `${game.htScore.home}–${game.htScore.away}` : null],
            [
              'Penalties',
              game.penaltyScore ? `${game.penaltyScore.home}–${game.penaltyScore.away}` : null
            ],
            ['Winner', game.winner || null],
            ['League', game.dataInfo?.leagueId === '98' ? 'J1 (level → penalties)' : 'Other']
          ]}
        />
        <Facts
          rows={[
            ['Kicked off', timeOf(game.kickedOffAt)],
            ['Half-time', timeOf(game.halfTimeStartedAt)],
            ['Second half', timeOf(game.secondHalfStartedAt)],
            ['Shootout', game.penaltiesStartedAt ? timeOf(game.penaltiesStartedAt) : null],
            ['Finished', timeOf(game.finishedAt)]
          ]}
        />
      </div>

      <div className="mt-4 border-t border-[#E2E8F0] pt-4">
        <div className="flex items-baseline justify-between gap-2">
          <h3 className="text-[13px] font-semibold text-[#0F172A]">Jump to</h3>
          <span className="text-[12px] text-[#64748B]">
            The match driver applies a jump on its next tick, within 30 seconds.
          </span>
        </div>
        {forced ? (
          <p className="mt-2 text-[12px] text-[#64748B]">
            This game was created as a straight penalty shootout, so it has no match clock to move.
            Create it again without that option to play a match.
          </p>
        ) : (
          <>
            <div className="mt-2 flex flex-wrap gap-2">
              {JUMPS.map((jump) => (
                <Btn
                  key={jump.key}
                  title={jump.hint}
                  disabled={busy}
                  onClick={() => onUpdate({ phase: jump.phase, minute: jump.minute }, jump.label)}
                >
                  {jump.label}
                </Btn>
              ))}
            </div>
            <div className="mt-2 flex items-center gap-2">
              <input
                aria-label="Match minute"
                placeholder="Minute 0–90"
                inputMode="numeric"
                className={`${controlClass} w-28`}
                value={customMinute}
                onChange={(e) => setCustomMinute(e.target.value.replace(/[^0-9]/g, ''))}
              />
              <Btn
                disabled={busy || !custom}
                onClick={() => onUpdate(custom, `minute ${customMinute}`)}
              >
                Go to minute
              </Btn>
            </div>
            <p className="mt-2 text-[12px] text-[#64748B]">
              Jumping back clears what happens again (kickoff, half-time, the result); back to
              pre-match also resets the score. Points already awarded stay awarded.
            </p>
          </>
        )}
      </div>

      <div className="mt-4 border-t border-[#E2E8F0] pt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div>
          <h3 className="text-[13px] font-semibold text-[#0F172A]">Goals</h3>
          <p className="text-[12px] text-[#64748B] mt-0.5">
            Scored on the next tick of play, with the goal event and push.
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Btn
              disabled={busy || forced || finished}
              onClick={() => onUpdate({ goal: 'home' }, 'Home goal')}
            >
              + {game.homeName}
            </Btn>
            <Btn
              disabled={busy || forced || finished}
              onClick={() => onUpdate({ goal: 'away' }, 'Away goal')}
            >
              + {game.awayName}
            </Btn>
          </div>
          {(queued.home > 0 || queued.away > 0) && (
            <p className="mt-2 text-[12px] text-[#1E40AF]">
              Waiting to score: home {queued.home}, away {queued.away}
              {['HT', 'P'].includes(game.gameStatus) || !game.gameStatus
                ? ' — held until play resumes'
                : ''}
            </p>
          )}
        </div>

        <div>
          <h3 className="text-[13px] font-semibold text-[#0F172A]">Set score</h3>
          <p className="text-[12px] text-[#64748B] mt-0.5">
            Written as is, without goal events — e.g. a level score for penalties.
          </p>
          <div className="mt-2 flex items-center gap-2">
            <input
              aria-label="Home score"
              inputMode="numeric"
              className={`${controlClass} w-14 text-center`}
              value={score.home}
              onChange={(e) => setScore({ ...score, home: e.target.value.replace(/[^0-9]/g, '') })}
            />
            <span className="text-[#64748B]">–</span>
            <input
              aria-label="Away score"
              inputMode="numeric"
              className={`${controlClass} w-14 text-center`}
              value={score.away}
              onChange={(e) => setScore({ ...score, away: e.target.value.replace(/[^0-9]/g, '') })}
            />
            <Btn
              disabled={busy || score.home === '' || score.away === ''}
              onClick={() =>
                onUpdate({ score: { home: Number(score.home), away: Number(score.away) } }, 'Score')
              }
            >
              Set
            </Btn>
          </div>
        </div>

        <div>
          <h3 className="text-[13px] font-semibold text-[#0F172A]">Behaviour</h3>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="lab-ctl-chance">Random goals</Label>
              <select
                id="lab-ctl-chance"
                className={`${controlClass} w-full`}
                disabled={busy}
                value={config.goalChance ?? 0.15}
                onChange={(e) =>
                  onUpdate({ mockConfig: { goalChance: Number(e.target.value) } }, 'Random goals')
                }
              >
                {GOAL_CHANCES.some((c) => c.chance === (config.goalChance ?? 0.15)) ? null : (
                  <option value={config.goalChance}>{Math.round(config.goalChance * 100)}%</option>
                )}
                {GOAL_CHANCES.map((c) => (
                  <option key={c.chance} value={c.chance}>
                    {c.value}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="lab-ctl-scorer">Who scores</Label>
              <select
                id="lab-ctl-scorer"
                className={`${controlClass} w-full`}
                disabled={busy}
                value={config.scoringTeam || 'random'}
                onChange={(e) =>
                  onUpdate({ mockConfig: { scoringTeam: e.target.value } }, 'Who scores')
                }
              >
                {SCORING_TEAMS.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.value}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <label className="mt-2 flex items-center gap-2 text-[13px] text-[#334155]">
            <input
              type="checkbox"
              disabled={busy}
              checked={config.showStatistics !== false}
              onChange={(e) =>
                onUpdate({ mockConfig: { showStatistics: e.target.checked } }, 'Statistics')
              }
            />
            Live match statistics
          </label>
        </div>
      </div>

      <div className="mt-4 border-t border-[#E2E8F0] pt-4 flex justify-end">
        <Btn variant="danger" disabled={busy} onClick={onDelete}>
          Delete this game
        </Btn>
      </div>
    </Card>
  );
};

export default GameControls;
